import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  MapPin, 
  DollarSign, 
  User, 
  Calendar, 
  AlertTriangle,
  Send,
  CheckCircle,
  XCircle,
  MessageSquare,
  Eye,
  Loader2
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { servicesService, serviceBidsService } from '../services/api';

interface Service {
  id: number;
  title: string;
  description: string;
  category: string;
  location: string;
  budget: number;
  currency: string;
  deadline: string;
  requester_id: number;
  requester_name: string;
  accepted_provider_id: number | null;
  status: 'open' | 'assigned' | 'completed' | 'cancelled';
  is_urgent: boolean;
  requirements: string[];
  bid_count: number;
  created_at: string;
}

interface Bid {
  id: number;
  service_id: number;
  provider_id: number;
  bid_amount: number;
  currency: string;
  estimated_duration: string;
  proposal: string;
  status: 'pending' | 'accepted' | 'rejected';
  provider_name: string;
  provider_rating?: number;
  submitted_at: string;
}

interface BidFormData {
  bid_amount: number;
  currency: string;
  estimated_duration: string;
  proposal: string;
}

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [service, setService] = useState<Service | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [myBid, setMyBid] = useState<Bid | null>(null);
  const [loading, setLoading] = useState(true);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [myBidLoading, setMyBidLoading] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [processingBid, setProcessingBid] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBidForm, setShowBidForm] = useState(false);
  
  const [bidFormData, setBidFormData] = useState<BidFormData>({
    bid_amount: 0,
    currency: 'KSH',
    estimated_duration: '',
    proposal: ''
  });

  const isServiceProvider = user?.roles?.some(role => role.name === 'service_provider');
  const isServiceOwner = service?.requester_id === user?.id;
  const canSubmitBid = isServiceProvider && !isServiceOwner && service?.status === 'open' && !myBid;
  const canViewBids = isServiceOwner || user?.roles?.some(role => role.name === 'admin');

  const loadService = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await servicesService.getServiceById(id);
      setService(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load service');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadBids = useCallback(async () => {
    if (!id || !canViewBids) return;
    
    try {
      setBidsLoading(true);
      const response = await serviceBidsService.getBidsForService(id);
      setBids(response.data);
    } catch (error: any) {
      console.error('Failed to load bids:', error);
    } finally {
      setBidsLoading(false);
    }
  }, [id, canViewBids]);

  const loadMyBid = useCallback(async () => {
    if (!id || !isServiceProvider || !user?.id) return;
    
    try {
      setMyBidLoading(true);
      const response = await serviceBidsService.getMyBids({ limit: 100 });
      // Find the bid for this specific service
      const serviceBid = response.data?.find((bid: any) => bid.service_id === parseInt(id));
      setMyBid(serviceBid || null);
    } catch (error: any) {
      console.error('Failed to load my bid:', error);
    } finally {
      setMyBidLoading(false);
    }
  }, [id, isServiceProvider, user?.id]);

  useEffect(() => {
    loadService();
  }, [loadService]);

  useEffect(() => {
    if (service) {
      loadBids();
      loadMyBid();
    }
  }, [loadBids, loadMyBid, service]);

  const handleBidFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBidFormData(prev => ({
      ...prev,
      [name]: name === 'bid_amount' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmitBid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !service) return;

    try {
      setSubmittingBid(true);
      await serviceBidsService.createBid({
        service_id: id,
        ...bidFormData
      });

      setShowBidForm(false);
      setBidFormData({
        bid_amount: 0,
        currency: 'KSH',
        estimated_duration: '',
        proposal: ''
      });
      
      // Reload bids, service and my bid to get updated data
      await Promise.all([loadBids(), loadService(), loadMyBid()]);
      
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const handleApproveBid = async (bidId: number) => {
    try {
      setProcessingBid(bidId);
      await serviceBidsService.acceptBid(bidId.toString());
      
      // Reload service and bids
      await Promise.all([loadService(), loadBids()]);
      
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to approve bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const handleRejectBid = async (bidId: number) => {
    try {
      setProcessingBid(bidId);
      await serviceBidsService.rejectBid(bidId.toString());
      
      // Reload bids
      await loadBids();
      
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reject bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'KSH') => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency === 'KSH' ? 'KES' : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string, isUrgent: boolean = false) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    const urgentClass = isUrgent ? " border-2 border-red-400" : "";

    switch (status) {
      case 'open':
        return `${baseClasses} bg-green-100 text-green-800${urgentClass}`;
      case 'assigned':
        return `${baseClasses} bg-blue-100 text-blue-800${urgentClass}`;
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800${urgentClass}`;
    }
  };

  const getBidStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded text-xs font-medium";

    switch (status) {
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'accepted':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-700">
              <p className="font-medium">Error loading service</p>
              <p className="text-sm mt-1">{error}</p>
              <button
                onClick={() => navigate('/services')}
                className="mt-4 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded"
              >
                Back to Services
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">Service not found</p>
            <Link 
              to="/services"
              className="mt-4 inline-block text-blue-600 hover:text-blue-700"
            >
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/services')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{service.title}</h1>
                <div className="flex items-center space-x-4 mt-2">
                  <span className={getStatusBadge(service.status, service.is_urgent)}>
                    {service.status}
                    {service.is_urgent && ' • URGENT'}
                  </span>
                  <span className="text-gray-500 text-sm">
                    Posted {formatDate(service.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            {canSubmitBid && (
              <button
                onClick={() => setShowBidForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Submit Bid
              </button>
            )}
            
            {isServiceProvider && !isServiceOwner && myBid && (
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  myBid.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  myBid.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  Bid {myBid.status}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  {formatCurrency(myBid.bid_amount, myBid.currency)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-700">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Service Details */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Service Details</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{service.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Category</h3>
                    <p className="text-gray-600 capitalize">{service.category.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600 flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      {service.location}
                    </p>
                  </div>
                </div>

                {service.requirements.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Requirements</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      {service.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* My Bid Section (Service Providers Only) */}
            {isServiceProvider && !isServiceOwner && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">My Bid</h2>

                {myBidLoading ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-blue-600" />
                    <p className="text-gray-600 text-sm">Loading your bid...</p>
                  </div>
                ) : myBid ? (
                  <div className={`border rounded-lg p-4 ${
                    myBid.status === 'accepted' ? 'border-green-300 bg-green-50' : 
                    myBid.status === 'rejected' ? 'border-red-300 bg-red-50' :
                    'border-blue-300 bg-blue-50'
                  }`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">Your Bid</h3>
                          <span className={getBidStatusBadge(myBid.status)}>
                            {myBid.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Submitted {formatDate(myBid.submitted_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(myBid.bid_amount, myBid.currency)}
                        </p>
                        {myBid.estimated_duration && (
                          <p className="text-sm text-gray-500 flex items-center justify-end">
                            <Clock className="h-3 w-3 mr-1" />
                            {myBid.estimated_duration}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <h4 className="font-medium text-gray-900 mb-2">Your Proposal</h4>
                      <p className="text-gray-600 whitespace-pre-wrap text-sm">{myBid.proposal}</p>
                    </div>

                    {myBid.status === 'pending' && (
                      <div className="bg-blue-100 border border-blue-200 rounded p-3">
                        <p className="text-blue-800 text-sm">
                          <span className="font-medium">Status:</span> Your bid is under review. 
                          You'll receive an email notification once the service requester makes a decision.
                        </p>
                      </div>
                    )}

                    {myBid.status === 'accepted' && (
                      <div className="bg-green-100 border border-green-200 rounded p-3">
                        <p className="text-green-800 text-sm">
                          <span className="font-medium">Congratulations!</span> Your bid has been accepted. 
                          You can now proceed with providing the service.
                        </p>
                      </div>
                    )}

                    {myBid.status === 'rejected' && (
                      <div className="bg-red-100 border border-red-200 rounded p-3">
                        <p className="text-red-800 text-sm">
                          <span className="font-medium">Bid not selected:</span> The service requester chose a different provider. 
                          Keep looking for other opportunities!
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 border border-gray-200 border-dashed rounded-lg">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500 mb-3">You haven't submitted a bid yet</p>
                    {canSubmitBid && (
                      <button
                        onClick={() => setShowBidForm(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        Submit Your Bid
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Bids Section */}
            {canViewBids && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Bids ({bids.length})
                </h2>

                {bidsLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-600" />
                    <p className="text-gray-600">Loading bids...</p>
                  </div>
                ) : bids.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No bids submitted yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bids.map((bid) => (
                      <div
                        key={bid.id}
                        className={`border rounded-lg p-4 ${
                          bid.status === 'accepted' ? 'border-green-300 bg-green-50' : 
                          bid.status === 'rejected' ? 'border-red-300 bg-red-50' :
                          'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium text-gray-900">{bid.provider_name}</h3>
                              <span className={getBidStatusBadge(bid.status)}>
                                {bid.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              Submitted {formatDate(bid.submitted_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-900">
                              {formatCurrency(bid.bid_amount, bid.currency)}
                            </p>
                            {bid.estimated_duration && (
                              <p className="text-sm text-gray-500 flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {bid.estimated_duration}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mb-4">
                          <h4 className="font-medium text-gray-900 mb-2">Proposal</h4>
                          <p className="text-gray-600 whitespace-pre-wrap">{bid.proposal}</p>
                        </div>

                        {isServiceOwner && bid.status === 'pending' && service.status === 'open' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApproveBid(bid.id)}
                              disabled={processingBid === bid.id}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              {processingBid === bid.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1 inline" />
                                  Approve
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleRejectBid(bid.id)}
                              disabled={processingBid === bid.id}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              {processingBid === bid.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 mr-1 inline" />
                                  Reject
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Service Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Info</h2>
              
              <div className="space-y-4">
                <div className="flex items-center text-gray-600">
                  <DollarSign className="h-5 w-5 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(service.budget, service.currency)}
                    </p>
                    <p className="text-sm">Budget</p>
                  </div>
                </div>

                <div className="flex items-center text-gray-600">
                  <User className="h-5 w-5 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{service.requester_name}</p>
                    <p className="text-sm">Service Requester</p>
                  </div>
                </div>

                {service.deadline && (
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-5 w-5 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatDate(service.deadline)}
                      </p>
                      <p className="text-sm">Deadline</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center text-gray-600">
                  <Eye className="h-5 w-5 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{service.bid_count}</p>
                    <p className="text-sm">Bids Received</p>
                  </div>
                </div>

                {service.is_urgent && (
                  <div className="flex items-center text-orange-600">
                    <AlertTriangle className="h-5 w-5 mr-3" />
                    <div>
                      <p className="font-medium">Urgent Request</p>
                      <p className="text-sm">Requires immediate attention</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {isServiceOwner && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
                <div className="space-y-2">
                  {service.status === 'open' && (
                    <>
                      <button
                        onClick={() => navigate(`/services/${service.id}/edit`)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium"
                      >
                        Edit Service
                      </button>
                      <button
                        onClick={() => {/* Handle cancel */}}
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium"
                      >
                        Cancel Service
                      </button>
                    </>
                  )}
                  {service.status === 'assigned' && (
                    <button
                      onClick={() => {/* Handle complete */}}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium"
                    >
                      Mark as Complete
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bid Submission Modal */}
      {showBidForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Submit Your Bid</h2>
              
              <form onSubmit={handleSubmitBid} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bid Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="flex">
                    <select
                      name="currency"
                      value={bidFormData.currency}
                      onChange={handleBidFormChange}
                      className="border border-gray-300 rounded-l-md px-3 py-2 text-sm"
                    >
                      <option value="KSH">KSH</option>
                      <option value="USD">USD</option>
                    </select>
                    <input
                      type="number"
                      name="bid_amount"
                      value={bidFormData.bid_amount || ''}
                      onChange={handleBidFormChange}
                      required
                      min="1"
                      className="flex-1 border border-l-0 border-gray-300 rounded-r-md px-3 py-2"
                      placeholder="Enter your bid amount"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Duration
                  </label>
                  <input
                    type="text"
                    name="estimated_duration"
                    value={bidFormData.estimated_duration}
                    onChange={handleBidFormChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., 2 weeks, 3 days, 1 month"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proposal <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="proposal"
                    value={bidFormData.proposal}
                    onChange={handleBidFormChange}
                    required
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Describe how you plan to complete this service, your experience, and why you're the best choice..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBidForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingBid}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
                  >
                    {submittingBid ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2 inline" />
                        Submit Bid
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;